import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  updateBugReportAttachments,
  removeBugReportAttachment,
} from "core/bugReport";
import { useAuth } from "hooks/auth/useAuth";
import { useState, useCallback } from "react";
import {
  Comment,
  IBugReport,
  ICreateBugReport,
  Attachment,
} from "@freetech/models";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/config/firebase";

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "text/plain",
];

interface UseStakeholderBugReportProps {
  projectId: string;
  clientId: string;
}

export const useStakeholderBugReport = ({
  clientId,
  projectId,
}: UseStakeholderBugReportProps) => {
  const { userInfo } = useAuth();
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);


  const { data: bugReports, isLoading: isLoadingReports } = useQuery({
    queryKey: ["bugReports"],
    queryFn: async () => {
      if (!clientId || !userInfo) {
        return [];
      }

      const q = query(
        collection(db, "bugReports"),
        where("clientId", "==", clientId)
      );

      const reports = await getDocs(q);

      return reports.docs.map((doc) => {
        const data = doc.data() as IBugReport;
        return {
          ...data,
          id: doc.id,
        };
      });
    },
    select: (data) => data,
    enabled: !!clientId && !!userInfo,
  });

  const createReport = useMutation<string, Error, ICreateBugReport>({
    mutationFn: async (data) => {
      const docRef = await addDoc(collection(db, "bugReports"), {
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: "open",
      });

      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bugReports"] });
    },
  });

  const updateAttachments = useMutation<
    void,
    Error,
    { bugReportId: string; files: File[] }
  >({
    mutationFn: ({ bugReportId, files }) =>
      updateBugReportAttachments(bugReportId, files),    
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bugReports"] });
    },
  });


  const removeAttachment = useMutation<
    void,
    Error,
    { bugReportId: string; attachmentId: string }
  >({
    mutationFn: ({ bugReportId, attachmentId }) =>
      removeBugReportAttachment(bugReportId, attachmentId),  
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bugReports"] });
      queryClient.invalidateQueries({ queryKey: ["bugReport", clientId] });
    },
  });


  const addComment = useMutation({
    mutationFn: async ({
      bugReportId,
      content,
      mentionedUserIds,
    }: {
      bugReportId: string;
      content: string;
      mentionedUserIds?: string[];
    }) => {
      if (!userInfo) throw new Error("User info not found");

      const newComment: Comment = {
        id: crypto.randomUUID(),
        content,
        userId: userInfo.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        mentionedUserIds,
      };

      const docRef = doc(db, "bugReports", bugReportId);
      const bugReport = bugReports?.find((report) => report.id === bugReportId);

      if (!bugReport) throw new Error("Bug report not found");

      await updateDoc(docRef, {
        comments: [...(bugReport.comments || []), newComment],
        updatedAt: new Date().toISOString(),
      });

      return newComment;
    },
    onSuccess: (_, { bugReportId }) => {
      queryClient.invalidateQueries({ queryKey: ["bugReports"] });
      queryClient.invalidateQueries({ queryKey: ["bugReport", bugReportId] });
    },
  });

  const handleFileSelect = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {

      if (rejectedFiles.length > 0) {
        const errorMessages = rejectedFiles
          .map(
            ({ file, errors }) =>
              `${file.name}: ${errors.map((e: any) => e.message).join(", ")}`
          )
          .join("; ");
        setUploadError(errorMessages);
        return;
      }


      const invalidFiles = acceptedFiles.filter(
        (file) =>
          !ALLOWED_FILE_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE
      );

      if (invalidFiles.length > 0) {
        setUploadError(
          `Invalid files: ${invalidFiles.map((f) => f.name).join(", ")}. Files must be under 10MB and of type: ${ALLOWED_FILE_TYPES.join(", ")}`
        );
        return;
      }

      setUploadError(null);
      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
    },
    []
  );

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (selectedFiles.length === 1) {
      setUploadError(null);
    }
  };

  const resetFiles = () => {
    setSelectedFiles([]);
    setUploadError(null);
  };

  const submitBugReport = async (data: ICreateBugReport, files?: File[]) => {
    try {

      const bugReportId = await createReport.mutateAsync(data);


      if (files && files.length > 0) {
        try {
          await updateAttachments.mutateAsync({
            bugReportId,
            files,
          });
        } catch (error) {
          console.error("Error updating attachments:", error);
        }
      }

      return bugReportId;
    } catch (error) {
      throw error;
    }
  };

  return {
    bugReports,
    isLoadingReports,
    submitBugReport,
    isSubmitting: createReport.isPending || updateAttachments.isPending,

    selectedFiles,
    uploadError,
    setUploadError,
    handleFileSelect,
    handleRemoveFile,
    resetFiles,

    addComment: addComment.mutateAsync,
    isAddingComment: addComment.isPending,

    removeAttachment: removeAttachment.mutateAsync,
    isRemovingAttachment: removeAttachment.isPending,
  };
};
